//sitemap
import * as THREE from 'three';
import { getMouseCoords } from 'root/animator/coords/mouse'
import { Lerp } from 'root/animator/tween/easing'
import Tween from 'root/animator/tween/tween'
import Ease from 'root/animator/tween/easing'

import logoImage from '../assets/logo.png'
import lightMap from '../assets/light-3.png'
import { screens } from 'root/components/sections/constants'
import { dispatchSetChanging, onMobile } from '../utils'

import { layers } from '../constants';

class Object3 {
    instance = new THREE.Group()
    points: any
    geometry: any
    material: any
    shader: any

    star: any

    logo: any

    state = {
        changing: false,
        visible: false,
        rotated: false
    }

    position = {
        visible: [0, 0, .3],
        hidden: [0, 0, 30]
    }
    currentRotation = {
        x: 0,
        y: 0,
        z: 0
    }

    mouse = {
        x: 0,
        y: 0
    }

    name = "Galaxy"

    constructor(scene) {
        this.init()
        scene.add(this.instance)
    }

    init() {
        this.shader = {
            uniforms: {
              iTime: { value: 0 },
              iResolution: { value: {
                x: window.innerWidth * window.devicePixelRatio,
                y: window.innerHeight * window.devicePixelRatio
              }}
            },
            vertexShader: `
            attribute vec3 randoms;
            attribute float inside;
            uniform float iTime;
            uniform vec2 iResolution;
            varying vec3 color;
            varying float z;
            varying float t;
        
            #define POINTSIZE 40.
            #define R_2 .34
            #define R_2_DISTOR 2.
            #define R_2_POINTSIZE .5
        
            vec3 hsv2rgb(vec3 c) {
              vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
              vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
              return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
            }
        
            void main() {
              float size = POINTSIZE * (1. + randoms.x);
              t = fract(randoms.y + .2 * iTime);
              float r = pow(.2 + min(.7, .8 * t), .3);
              size *= pow(t,.5) * smoothstep(.8, .7, t);
              if (inside < 0.) {
                r = mix(R_2, .0, pow(t, R_2_DISTOR));
                size = size * R_2_POINTSIZE;
              }
              vec4 vpos = modelViewMatrix * vec4(position * r, 1.);
              color = hsv2rgb(vec3(mix(1., 183., pow(randoms.z, .45)) / 360., .65, .7));
              z = dot(normalize(cameraPosition), position);
              gl_PointSize = size * iResolution.y / 900. / -vpos.z;
              gl_Position = projectionMatrix * vpos;
            }`,
            fragmentShader: `
            varying vec3 color;
            varying float z;
            varying float t;
        
            void main() {
              float d = length(2. * gl_PointCoord - 1.);
        
              float t2 = max(0., 2.*fract(t) - .66);
              d = d + t2; // point evolution
        
              float tex = pow(smoothstep(1., .25, d), 3.);
              float fade = clamp(.7 * z + .8, 0., 1.);
              gl_FragColor = vec4(pow(color * tex * fade, vec3(1./2.2)), tex );
            }`
          }
        this.geometry = new THREE.BufferGeometry();
        let galaxyPositions = [];
        let galaxyRandoms = [];
        let galaxyInside = [];
        for (let i = 0; i < 6000; i++) {
            let u = Math.random();
            let v = Math.random();
            let theta = u * 2.0 * Math.PI;
            let phi = Math.acos(2.0 * v - 1.0);
            //let r = Math.cbrt(Math.random());
            let sinPhi = Math.sin(phi);
            let x = sinPhi * Math.cos(theta);
            let y = sinPhi * Math.sin(theta);
            let z = Math.cos(phi);
            galaxyPositions.push(x, y, z);
            galaxyRandoms.push(Math.random(), Math.random(), Math.random());
            galaxyInside.push(i < 3500 ? 1 : -1);
        }
        this.geometry.setAttribute('position', new THREE.Float32BufferAttribute(galaxyPositions, 3));
        this.geometry.setAttribute('randoms', new THREE.Float32BufferAttribute(galaxyRandoms, 3));
        this.geometry.setAttribute('inside', new THREE.Float32BufferAttribute(galaxyInside, 1));

        this.material = new THREE.ShaderMaterial({
            vertexShader: this.shader.vertexShader,
            fragmentShader: this.shader.fragmentShader,
            uniforms: this.shader.uniforms,
            depthTest: false,
            transparent: true
        });
        this.material.stencil = false;

        this.points = new THREE.Points(this.geometry, this.material)
        this.points.rotation.set(
            this.currentRotation.x,
            this.currentRotation.y,
            this.currentRotation.z,
        )
        onMobile(() => {
            this.points.scale.set(0.7, 0.7, 0.7)
        })
        this.points.layers.enable(layers.ENTIRE_SCENE)

        this.logo = new Logo( this.points )
        this.star = new BgLight( this.instance )

        this.instance.visible = false
        this.instance.position.set(...this.position.hidden)
        this.instance.name = this.name
        this.instance.add( this.points )

    }

    resize() {
        this.shader.uniforms['iResolution'].value.x = window.innerWidth * window.devicePixelRatio;
        this.shader.uniforms['iResolution'].value.y = window.innerHeight * window.devicePixelRatio;
    }

    controls() {
        if (this.mouse.x === null || this.mouse.y === null) { return }
        const mouse = { x: 0, y: 0 }
        mouse.x = ( this.mouse.x - window.innerWidth / 2 ) / window.innerWidth
        mouse.y = ( this.mouse.y - window.innerHeight / 2 ) / window.innerHeight
        this.currentRotation.x = Lerp(this.currentRotation.x, mouse.x)
        this.currentRotation.y = Lerp(this.currentRotation.y, mouse.y)
        this.points.rotation.set(this.currentRotation.y / -2, this.currentRotation.x / -2, 0)
    }

    render__moveIn() {
        const EaseOutCustom = (t) => Ease.OutCustom(t, 4)
        this.render__move(
            this.position.visible[2],
            EaseOutCustom,
            () => { this.state.visible = true }
        )
    }
    render__moveOut() {
        const EaseInCustom = (t) => Ease.InCustom(t, 4)
        this.render__move(
            this.position.hidden[2],
            EaseInCustom,
            () => { this.state.visible = false }
        )
    }
    render__move(to: number, ease = Ease.InCubic, onComplete = () => {}) {
        this.state.changing = true
        dispatchSetChanging(true, 0)
        Tween.to(this.instance.position.z, to,
            {
                renderDelay: 10,
                duration: 3000,
                ease: ease,
                onChange: function(context) {
                    this.instance.position.z = context.value
                }.bind(this),
                onComplete: function() { 
                    this.state.changing = false
                    dispatchSetChanging(false)
                    onComplete()
                }.bind(this)
            }
        )
    }

    render__rotateIn_Partners() {
        const to = {
            x: Math.PI / 5,
            y: Math.PI / 3
        }
        this.render__rotate(to)
    }
    render__rotateIn_Roadmap() {
        const to = {
            x: Math.PI / 3,
            y: 0
        }
        this.render__rotate(to)
    }
    render__rotateIn_Investors() {
        const to = {
            x: Math.PI / -5,
            y: Math.PI / 3
        }
        this.render__rotate(to)
    }
    render__rotateIn_Socials() {
        const to = {
            x: Math.PI / -5,
            y: Math.PI / -3
        }
        this.render__rotate(to)
    }
    render__rotateIn_Product() {
        const to = {
            x: Math.PI / 5,
            y: Math.PI / -3
        }
        this.render__rotate(to)
    }

    render__rotateOut() {
        const to = {
            x: 0,
            y: 0
        }
        this.render__rotate(to, () => this.state.rotated = false)
    }
    render__rotate(to: any, onComplete = () => {}) {
        this.state.changing = true
        this.state.rotated = true
        const EaseInOutCustom = (t) => Ease.InOutCustom(t, 4)
        Tween.to({ x: this.instance.rotation.x, y: this.instance.rotation.y }, to,
            {
                renderDelay: 10,
                duration: 2000,
                ease: EaseInOutCustom,
                onChange: function(context) {
                    this.instance.rotation.x = context.value.x
                    this.instance.rotation.y = context.value.y
                }.bind(this),
                onComplete: function() { 
                    onComplete()
                }.bind(this)
            }
        )
    }

    render({ visible = false, screen = screens.NONE }) {

        if ( !this.state.changing ) {
            if ( !visible && this.state.visible ) {
                this.render__moveOut()
            } else
            if ( visible && !this.state.visible ) {
                this.render__moveIn()
                this.render__rotateOut()
            }


            if ( !this.state.rotated && screen === screens.PARTNERS ) {
                this.render__rotateIn_Partners()
            } else 
            if ( !this.state.rotated && screen === screens.ROADMAP ) {
                this.render__rotateIn_Roadmap()
            } else
            if ( !this.state.rotated && screen === screens.INVESTORS ) {
                this.render__rotateIn_Investors()
            } else
            if ( !this.state.rotated && screen === screens.SOCIAL ) {
                this.render__rotateIn_Socials()
            } else
            if ( !this.state.rotated && screen === screens.PRODUCT ) {
                this.render__rotateIn_Product()
            }
        }

        if ( this.state.visible || visible) {
            const t = performance.now() / 1000
            this.shader.uniforms.iTime.value = t
            this.logo.render()
            this.mouse = getMouseCoords().window
            this.instance.visible = true
        } else {
            this.instance.visible = false
            this.state.changing = false
        }
    }
}

export default Object3



class Logo {
    geometry    = null
    material    = null
    instance    = null

    constructor(scene) {
        this.geometry = new THREE.PlaneGeometry( 0.4, 0.4, 1, 1 )
        this.material = new THREE.MeshBasicMaterial({
            transparent: true,
            opacity: 1,
            side: THREE.DoubleSide,
            depthTest: false,
            map: new THREE.TextureLoader().load( logoImage ),
        })
        this.instance = new THREE.Mesh( this.geometry, this.material )
        this.instance.position.set(0, 0, 0)
        this.instance.layers.enable(layers.ENTIRE_SCENE)
        scene.add( this.instance )
    }

    render() {
        this.instance.rotation.y -= 0.02
    }
}

class BgLight {
    geometry    = null
    material    = null
    instance    = null

    constructor(scene) {
        this.geometry = new THREE.PlaneGeometry( 1, 1, 1, 1 )
        this.material = new THREE.MeshBasicMaterial({
            transparent: true,
            opacity: 1,
            side: THREE.DoubleSide,
            depthTest: false,
            map: new THREE.TextureLoader().load( lightMap )
        })
        this.instance = new THREE.Mesh( this.geometry, this.material )
        this.instance.position.set(0, 0, -.1)
        this.instance.scale.set(2, 2, 2)
        this.instance.layers.enable(layers.BLOOM_SCENE)
        scene.add( this.instance )
    }
}